import { fetchClient } from "@babackapp/private-api-client/generated/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const untypedGet = fetchClient.GET as unknown as (
  url: string,
  opts?: { params?: { query?: Record<string, string | boolean | undefined> } },
) => Promise<{ data: unknown; error: unknown }>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const untypedPost = fetchClient.POST as unknown as (
  url: string,
  opts?: { body?: unknown },
) => Promise<{ data: unknown; error: unknown }>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const untypedPatch = fetchClient.PATCH as unknown as (
  url: string,
  opts?: { body?: unknown },
) => Promise<{ data: unknown; error: unknown }>;

export interface TopShopEntry {
  shopId: string;
  count: number;
}

export interface TrackingErrorSummary {
  total: number;
  byErrorCategory: Array<{
    errorCategory: string;
    count: number;
    topShops: TopShopEntry[];
  }>;
  byCarrier: Array<{
    carrierCode: string;
    count: number;
    topShops: TopShopEntry[];
  }>;
  byShop: Array<{
    shopId: string;
    count: number;
    topError: string;
    topCarrier: string;
  }>;
  bySeverity: Array<{ errorSeverity: string; count: number }>;
}

export interface TrackingJobError {
  id: string;
  createdAt: string;
  returnRequestId: string;
  shopId: string;
  trackingCode: string;
  carrierCode: string;
  errorClass: string;
  errorCategory: string;
  errorSeverity: string;
  errorMessage: string;
  source: string;
  jobId?: string;
  attemptsMade: number;
  failedAt: string;
  isResolved: boolean;
  resolvedAt?: string;
  resolvedNote?: string;
}

export interface TrackingErrorsResponse {
  data: TrackingJobError[];
  total: number;
}

export interface TrackingErrorsFilters {
  shopId?: string;
  carrierCode?: string;
  errorCategory?: string;
  errorSeverity?: string;
  isResolved?: boolean;
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}

const QUERY_KEY_SUMMARY = ["tracking-queue-errors-summary"];
const QUERY_KEY_ERRORS = (filters: TrackingErrorsFilters) => [
  "tracking-queue-errors",
  filters,
];

export function useTrackingErrorsSummary(filters?: {
  isResolved?: boolean;
  fromDate?: string;
  toDate?: string;
}) {
  return useQuery({
    queryKey: [...QUERY_KEY_SUMMARY, filters],
    queryFn: async () => {
      const { data, error } = await untypedGet(
        "/return-requests-tracking/errors/summary",
        {
          params: {
            query: {
              isResolved:
                filters?.isResolved !== undefined
                  ? String(filters.isResolved)
                  : undefined,
              fromDate: filters?.fromDate,
              toDate: filters?.toDate,
            },
          },
        },
      );
      if (error) throw error;
      return data as TrackingErrorSummary;
    },
    staleTime: 30_000,
  });
}

export function useTrackingErrors(filters: TrackingErrorsFilters) {
  return useQuery({
    queryKey: QUERY_KEY_ERRORS(filters),
    queryFn: async () => {
      const { data, error } = await untypedGet(
        "/return-requests-tracking/errors",
        {
          params: {
            query: {
              shopId: filters.shopId,
              carrierCode: filters.carrierCode,
              errorCategory: filters.errorCategory,
              errorSeverity: filters.errorSeverity,
              isResolved:
                filters.isResolved !== undefined
                  ? String(filters.isResolved)
                  : undefined,
              fromDate: filters.fromDate,
              toDate: filters.toDate,
              page: filters.page ? String(filters.page) : undefined,
              pageSize: filters.pageSize ? String(filters.pageSize) : undefined,
            },
          },
        },
      );
      if (error) throw error;
      return data as TrackingErrorsResponse;
    },
    staleTime: 30_000,
  });
}

export function useResolveTrackingError() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) => {
      const { data, error } = await untypedPatch(
        `/return-requests-tracking/errors/${id}/resolve`,
        { body: { note } },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY_SUMMARY });
      void queryClient.invalidateQueries({
        queryKey: ["tracking-queue-errors"],
      });
    },
  });
}

export interface CarrierRatioEntry {
  carrierCode: string;
  errorRequests: number;
  totalRequests: number;
  errorRate: number;
}

export function useCarrierRatio(shopId?: string) {
  return useQuery({
    queryKey: ["tracking-carrier-ratio", shopId ?? "global"],
    queryFn: async () => {
      const { data, error } = await untypedGet(
        "/return-requests-tracking/errors/carrier-ratio",
        { params: { query: { shopId } } },
      );
      if (error) throw error;
      return data as CarrierRatioEntry[];
    },
    staleTime: 60_000,
  });
}

export function useResolveByCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      shopId,
      carrierCode,
      errorCategory,
      note,
    }: {
      shopId: string;
      carrierCode: string;
      errorCategory: string;
      note?: string;
    }) => {
      const { data, error } = await untypedPost(
        "/return-requests-tracking/errors/resolve-by-category",
        { body: { shopId, carrierCode, errorCategory, note } },
      );
      if (error) throw error;
      return data as { count: number };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY_SUMMARY });
      void queryClient.invalidateQueries({
        queryKey: ["tracking-queue-errors"],
      });
    },
  });
}

export function useForceRetryByCategory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      shopId,
      carrierCode,
      errorCategory,
    }: {
      shopId: string;
      carrierCode: string;
      errorCategory: string;
    }) => {
      const { data, error } = await untypedPost(
        "/return-requests-tracking/errors/force-retry-by-category",
        { body: { shopId, carrierCode, errorCategory } },
      );
      if (error) throw error;
      return data as { count: number };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY_SUMMARY });
      void queryClient.invalidateQueries({
        queryKey: ["tracking-queue-errors"],
      });
    },
  });
}

export function useResolveByShopCarrier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      shopId,
      carrierCode,
      note,
    }: {
      shopId: string;
      carrierCode: string;
      note?: string;
    }) => {
      const { data, error } = await untypedPost(
        "/return-requests-tracking/errors/resolve-by-shop-carrier",
        { body: { shopId, carrierCode, note } },
      );
      if (error) throw error;
      return data as { count: number };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY_SUMMARY });
      void queryClient.invalidateQueries({
        queryKey: ["tracking-queue-errors"],
      });
    },
  });
}
